/**
 * **마감 세트** — 벽·징두리·바닥이 한 벌로 딸려오는 「이 방이 무엇인가」.
 *
 * (2026-09-14, Chris: 「벽지 색상이나 재질 고르는 게 어렵다 — 지능적으로」 · 「해당 겜 컨셉에 맞게
 * 만들어서 준비」). 색을 낱낱이 고르게 하지 않는다. 방 42개 중 24개가 관사 6채의 반복이라
 * 자유 색상표를 열면 여섯 채가 제각각이 되어 오히려 무너진다. 1987년 폐교를 고친 시골 마을에서
 * **실제로 쓸 수 있는 마감은 예닐곱 가지뿐**이다 — 그 안에서만 고른다.
 *
 * 표면 id 는 `client/src/materials.ts` 의 SurfaceId 와 같은 문자열이다(shared 라 타입을 못 가져온다).
 */
export type FinishSetId = 'school' | 'home' | 'kitchen' | 'office' | 'shop' | 'store';

export interface FinishSet {
  id: FinishSetId;
  name: string;
  /** 한 줄 설명 — 팝업에 그대로 뜬다 */
  desc: string;
  /** 허리선 위 벽 */
  wall: string;
  /** 허리선 아래. 없으면 벽 하나로 천장까지 */
  wains?: string;
  /** 허리선 높이(m). 몰딩의 chair rail(1.02)과 맞추는 것이 기본 */
  chairY?: number;
  /** 이 세트의 기본 바닥 */
  floor: string;
  /**
   * **방 조명** (2026-09-14, Chris: 「조명색도 그렇고」). 42개 방이 전부 형광등 흰빛 하나였다 —
   * 색온도가 같으면 마감을 아무리 갈라도 사진 찍으면 같은 방이다. 세트에 묶어 한 벌로 딸려온다.
   * `k` 는 세기 배율(어두운 방은 1 아래).
   */
  light?: { color: string; k?: number };
}

export const FINISH_SETS: FinishSet[] = [
  { id: 'school',  name: '분교 교실', desc: '회녹색 페인트 + 나무 징두리 · 마루', wall: 'paint_stain:sage', wains: 'wains_board:walnut', chairY: 1.02, floor: 'plank:oak', light: { color: '#e2e5e4', k: 1 } },
  { id: 'home',    name: '살림방',    desc: '누런 점무늬 벽지 · 장판',           wall: 'paper_dot:yellow', floor: 'lino:yellow', light: { color: '#efd6ae', k: 0.8 } },
  { id: 'kitchen', name: '주방·물쓰는 곳', desc: '흰 페인트 + 허리 타일 · 바닥 타일', wall: 'paint:white', wains: 'wains_tile:white', chairY: 1.2, floor: 'ftile_30:white', light: { color: '#ece6d6', k: 1 } },
  { id: 'office',  name: '사무공간',  desc: '흰 페인트 · 짙은 마루',             wall: 'paint:white', floor: 'plank:dark', light: { color: '#e4e9ec', k: 1.05 } },
  { id: 'shop',    name: '작업장',    desc: '거친 회벽 · 시멘트',                 wall: 'plaster_rough:warm', floor: 'cement:grey', light: { color: '#e8c48a', k: 0.75 } },
  { id: 'store',   name: '창고·설비', desc: '시멘트 블록 노출 · 콘크리트',        wall: 'block:grey', floor: 'concrete', light: { color: '#d9c39a', k: 0.6 } },
];
export const FINISH_BY_ID: Record<string, FinishSet> = Object.fromEntries(FINISH_SETS.map((f) => [f.id, f]));

/** 바닥재 이름표 — 팝업에 쓴다 */
export const FLOOR_NAMES: Record<string, string> = {
  oak: '마루', darkwood: '짙은 마루', vinyl: '장판', tile: '타일', tile_white: '흰 타일', concrete: '콘크리트', plaster: '회벽',
};

/**
 * **방 id·이름으로 세트를 미리 고른다.** 사람은 확인만 하거나 다른 세트로 바꾼다.
 * LLM 을 부르지 않는다 — 방 42개에 세트 6개면 규칙으로 충분하고, 매번 다른 답이 나오면 재현이 안 된다.
 */
export function suggestFinish(roomId: string, roomName = ''): FinishSetId {
  const id = roomId, n = roomName;
  if (/_kit$|_util$|kitchen|주방|다용도|암실|_dark$/.test(id + n)) return 'kitchen';
  if (/_bed$|_room$|_work$|침실|거실|업무공간/.test(id + n)) return 'home';
  if (/_gen$|발전|창고|설비|보일러/.test(id + n)) return 'store';
  if (/shop|studio|kiln|roast|목공|도예|가마|로스팅|작업/.test(id + n)) return 'shop';
  if (/office|meet|fin|arch|inv|intv|photo|사무|회의|재무|기록|수사|신문|사진/.test(id + n)) return 'office';
  return 'school';   // 현관·복도·카페 — 고친 교실 그대로
}

/** 건물 id → 사람이 읽는 이름. `BUILDINGS` 에는 이름이 없다 */
export const BUILDING_NAMES: Record<string, string> = {
  center: '조합센터', annex: '작업동', annex_kiln: '가마실동', gwansa: '관사',
  han: '한서진의 집', song: '송이경의 집', yun: '윤가람의 집', myeong: '명주원의 집', yuri: '차유리의 집', bae: '배정호의 집',
};
